import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, type JSX } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { getStartParam, resolveDeepLink } from "./lib/telegram.js";
import { trpc } from "./trpc.js";
import { Splash } from "./screens/Splash.js";
import { Consent } from "./screens/Consent.js";
import { Basics } from "./screens/Basics.js";
import { Onboarding } from "./screens/Onboarding.js";
import { AddPhoto } from "./screens/AddPhoto.js";
import { Assigned } from "./screens/Assigned.js";
import { Waiting } from "./screens/Waiting.js";
import { Home } from "./screens/Home.js";
import { Letter } from "./screens/Letter.js";
import { Profile } from "./screens/Profile.js";
import { Feedback } from "./screens/Feedback.js";
import { Sent } from "./screens/Sent.js";
import { Match } from "./screens/Match.js";
import { Places } from "./screens/Places.js";
import { Curator } from "./screens/Curator.js";
import { CuratorChat } from "./screens/CuratorChat.js";
import { Pause } from "./screens/Pause.js";
import { Privacy } from "./screens/Privacy.js";
import { Terms } from "./screens/Terms.js";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};
const pageTransition = { duration: 0.28, ease: [0.25, 0.1, 0.25, 1] as const };

/**
 * Сносит все локальные данные Companion (куратор, фото, флаги-«видел письмо»)
 * и кеш tRPC-запросов. Вызывается при ?reset=1 или startapp=reset —
 * чтобы тестер мог начать заново без переустановки мини-аппа.
 */
function wipeLocal(): void {
  if (typeof window === "undefined") return;
  try {
    const ls = window.localStorage;
    const toDrop: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const key = ls.key(i);
      if (key?.startsWith("companion:")) toDrop.push(key);
    }
    toDrop.forEach((k) => {
      ls.removeItem(k);
    });
    ls.removeItem("companion:self-photo");
    ls.removeItem("companion:curator-id");
    ls.removeItem("companion:seen-letter");
    window.sessionStorage.clear();
  } catch {
    /* privacy mode — идём дальше */
  }
}

export function App(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandled = useRef(false);
  const registrationChecked = useRef(false);

  // Reset-хак: ?reset=1 или startapp=reset чистят localStorage и редиректят
  // на Splash. Нужно для тестирования MVP в @companiondevbot без переустановки.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const resetViaQuery = url.searchParams.get("reset") === "1";
    const resetViaStart = getStartParam() === "reset";
    if (resetViaQuery || resetViaStart) {
      wipeLocal();
      url.searchParams.delete("reset");
      window.history.replaceState({}, "", url.pathname + url.search);
      navigate("/", { replace: true });
      // Форсим перезагрузку: tRPC-кеш в react-query должен забыть прошлого юзера.
      window.setTimeout(() => {
        window.location.reload();
      }, 50);
    }
  }, [navigate]);

  // Persistent регистрация: backend = источник истины. Если /me вернуло
  // onboardingCompleted=true, уводим на /home. Если false — чистим localStorage
  // (чтобы сбросить флаги от прошлого юзера) и оставляем на splash для онбординга.
  // Это фикс ситуации, когда DB почищена, а localStorage в Telegram WebView
  // залип с companion:registered=1 → юзер мгновенно падал в /home без анкеты.
  const meQ = trpc.me.get.useQuery(undefined, {
    enabled: location.pathname === "/",
    staleTime: 0,
    retry: 1,
  });
  useEffect(() => {
    if (registrationChecked.current) return;
    if (typeof window === "undefined") return;
    if (location.pathname !== "/") return;
    // Даём deep-link отработать первым.
    const deepTarget = resolveDeepLink(getStartParam());
    if (deepTarget) return;
    if (!meQ.data) return; // ждём ответ бекенда
    if (meQ.data.onboardingCompleted) {
      window.localStorage.setItem("companion:registered", "1");
      registrationChecked.current = true;
      navigate("/home", { replace: true });
    } else {
      // DB говорит «не проходил» — чистим следы прошлой сессии и остаёмся на splash.
      window.localStorage.removeItem("companion:registered");
      registrationChecked.current = true;
    }
  }, [location.pathname, navigate, meQ.data]);

  // Разовая отработка startapp-параметра из Telegram (letter_04 / match_02).
  // Запускаемся только на splash: если юзер уже где-то — не телепортим.
  useEffect(() => {
    if (deepLinkHandled.current) return;
    if (location.pathname !== "/") return;
    const param = getStartParam();
    if (param === "reset") return; // обрабатывается выше
    const target = resolveDeepLink(param);
    if (!target) return;
    deepLinkHandled.current = true;
    navigate(target, { replace: true });
  }, [location.pathname, navigate]);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={pageVariants}
        transition={pageTransition}
      >
        <Routes location={location}>
          <Route path="/" element={<Splash />} />
          <Route path="/consent" element={<Consent />} />
          <Route path="/basics" element={<Basics />} />
          <Route path="/photo" element={<AddPhoto />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/waiting" element={<Waiting />} />
          <Route path="/assigned" element={<Assigned />} />
          <Route path="/home" element={<Home />} />
          <Route path="/letter/:id" element={<Letter />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/feedback/:id" element={<Feedback />} />
          <Route path="/sent" element={<Sent />} />
          <Route path="/match/:id" element={<Match />} />
          <Route path="/places/:id" element={<Places />} />
          <Route path="/curator" element={<Curator />} />
          <Route path="/chat" element={<CuratorChat />} />
          <Route path="/pause" element={<Pause />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Splash />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}
